import { FC, useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import clsx from 'clsx'

import { Box, Button, Card, TextField } from '@mui/material'
import withStyles from '@mui/styles/withStyles'
import { DateRangePicker } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import { Context } from 'components/core/Acquiring/AcquiringOverview/components/AcquiringTransfersContext'
import { useStyles } from 'components/core/Acquiring/AcquiringOverview/components/styles'
import { useDateRangePicker } from 'hooks/useDateRangePicker'
import { finishAcquiringTransfersDownload, startAcquiringTransfersDownload } from 'redux/acquiring/acquiring-actions'
import { useAppSelector } from 'redux/hooks'
import { themeStyles } from 'themes'
import combineStyles from 'utils/combineStyles'

interface IProps {
    classes: Record<string, string>
}

const AcquiringTransfersDownload: FC<IProps> = ({ classes }) => {
    const dispatch = useDispatch()
    const { cardAcquiringChargesDownload: download } = useAppSelector(state => state.acquiring)
    const style = useStyles()
    const { t } = useTranslation(['override', 'translations', 'keys'])
    const { transfersInputs, setTransfersInputs } = useContext(Context)
    const { dateRangeValue, handleDateRangeValue, dateRangeStart, dateRangeEnd } = useDateRangePicker('YYYY-MM-DD')

    const [type, setType] = useState('')

    const Download = useCallback(
        file => {
            const element = document.createElement('a')

            element.href = URL.createObjectURL(file)
            element.download = `acquiring.${type}`
            document.body.appendChild(element)
            element.click()
            dispatch(finishAcquiringTransfersDownload())
        },
        [dispatch, finishAcquiringTransfersDownload, type]
    )

    useEffect(() => {
        setTransfersInputs(prevState => ({ ...prevState, from: dateRangeStart || '', to: dateRangeEnd || '' }))
    }, [dateRangeStart, dateRangeEnd])

    useEffect(() => {
        if (download) {
            const file = new Blob([download], { type: type === 'pdf' ? 'application/pdf' : 'text/csv' })

            download && Download(file)
        }
    }, [download, Download, type])

    const handleDownload = chosenType => {
        setType(chosenType)
        dispatch(startAcquiringTransfersDownload({ from: transfersInputs?.from, to: transfersInputs?.to, type: chosenType }))
    }

    return (
        <Card classes={{ root: classes.card }}>
            <Box display="flex" alignItems="center" className={clsx(classes.box, style.box)}>
                <div className="dateContainer">
                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        localeText={{ start: t('TransactionsFilter.From'), end: t('TransactionsFilter.To') }}
                    >
                        <DateRangePicker
                            value={dateRangeValue}
                            showToolbar={false}
                            onChange={handleDateRangeValue}
                            renderInput={(startProps, endProps) => {
                                startProps.inputProps = {
                                    ...startProps.inputProps,
                                    'data-sta-id': 'PaymentGateway-overview-dateRangePicker-startDate',
                                }
                                endProps.inputProps = {
                                    ...endProps.inputProps,
                                    'data-sta-id': 'PaymentGateway-overview-dateRangePicker-endDate',
                                }

                                return (
                                    <>
                                        <TextField className={style.dateRangeField} {...startProps} inputProps={startProps.inputProps} />
                                        <TextField className={style.dateRangeField} {...endProps} inputProps={endProps.inputProps} />
                                    </>
                                )
                            }}
                        />
                    </LocalizationProvider>
                </div>

                <Button
                    onClick={() => handleDownload('pdf')}
                    className={clsx(classes.filterButton, style.filterButton)}
                    variant="contained"
                    color="primary"
                >
                    {t('Acquiring.Download')} PDF
                </Button>
            </Box>
        </Card>
    )
}

export default withStyles(combineStyles(themeStyles.useAcquiringStyles, themeStyles.useTableStyles))(AcquiringTransfersDownload)
